import { useEffect, useRef, useState } from "react";
import { BOOKING_ENABLED, MAINTENANCE_SLUG, TEST_RIDE_SLUG, bookingUrl, calLink } from "@/lib/booking";
import { trackBookingCreated } from "@/lib/analytics";
import { color, radius } from "@/lib/design-tokens";

/**
 * The Cal.com booking calendar, embedded inline.
 *
 * WHY AN IFRAME AND NOT CAL'S EMBED SCRIPT
 *   Cal.com ships an embed.js that injects its own script, mutates the host
 *   page and phones home. This site currently loads NO third-party JavaScript
 *   at all, and script/smoke.mjs asserts zero console errors and zero failed
 *   requests on every route — a third-party script is the single most likely
 *   thing to break both, on someone else's deploy schedule.
 *
 *   The iframe gets the same calendar with none of that: no script on our page,
 *   nothing that can touch our DOM, and a failure mode that is one blank box
 *   rather than a broken page. It is also the reason this component can be
 *   rendered at all while BOOKING_ENABLED is false — there is nothing to
 *   initialise.
 *
 * WHAT IT DOES NOT DO
 *   It does not claim a booking has been made. Cal.com confirms in its own UI
 *   and by email. Nothing in this component may say "confirmed" or "booked" —
 *   the site's own copy has been wrong about that before.
 */
export function CalEmbed({ slug }: { slug?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const trackedBooking = useRef<string | null>(null);
  const [visible, setVisible] = useState(false);
  // The visual/geometry harnesses already use ?__freeze=1 to stop other live
  // UI. Keep the iframe on its documented fallback height there as well;
  // otherwise Cal's postMessage can land before or after a measurement based
  // on network timing and make the same source alternate between two heights.
  // Normal visits never carry this flag and still follow Cal's live height.
  const frozen =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("__freeze");
  // Fallback only — replaced by Cal's own reported height within ~2s. Sized to
  // the booker's header plus a month grid, not to a guess about slots.
  const [height, setHeight] = useState(920);

  // Cal.com posts its rendered height to the parent, and the frame follows it.
  //
  // THE SHAPE MATTERS AND I GOT IT WRONG FIRST. The message is
  //   { originator: "CAL", type: "__dimensionChanged",
  //     data: { iframeHeight, iframeWidth, isFirstTime } }
  // My first handler read `d.height` and `d.data.height`, neither of which
  // exists, so it never matched and the frame silently sat on its fallback —
  // 1182px at every viewport. That is a bad number twice over: measured live,
  // Cal asks for 1049-1253px on desktop and 758-962px on a phone. So the
  // calendar was clipped on desktop and floated in ~400px of dead white space
  // on mobile.
  //
  // Found by logging every postMessage the frame sent rather than assuming an
  // API shape. `isFirstTime: true` carries a generic 1180 before the booker
  // renders; the useful values arrive after.
  //
  // Origin-checked: any page can postMessage to this window, and a handler that
  // writes to state without checking is an open door.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      let host = "";
      try { host = new URL(e.origin).hostname; } catch { return; }
      if (!/(^|\.)cal\.com$/.test(host)) return;

      const d = e.data as {
        type?: string;
        data?: {
          iframeHeight?: number;
          uid?: string;
          startTime?: string;
          eventTypeId?: number;
          status?: string;
          isRecurring?: boolean;
        };
      };

      if (d?.type === "bookingSuccessfulV2") {
        const bookingKey = d.data?.uid || d.data?.startTime || "booking-created";
        if (trackedBooking.current !== bookingKey) {
          trackedBooking.current = bookingKey;
          trackBookingCreated(slug || TEST_RIDE_SLUG, {
            booking_status: d.data?.status,
            event_type_id: d.data?.eventTypeId,
            is_recurring: d.data?.isRecurring,
          });
        }
        return;
      }

      if (d?.type !== "__dimensionChanged") return;
      if (frozen) return;

      const h = Number(d.data?.iframeHeight);
      // Floor keeps a mid-transition collapse from snapping the page shut;
      // ceiling keeps a runaway value from producing an absurd frame.
      if (Number.isFinite(h) && h > 320 && h < 4000) setHeight(Math.ceil(h) + 8);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [frozen, slug]);

  // Load the calendar once it is near the viewport — it is the heaviest thing
  // on /book and most visitors are reading, not booking.
  //
  // BUT AN OBSERVER THAT CAN FAIL TO FIRE MUST NOT BE THE ONLY PATH. With just
  // the IntersectionObserver this rendered ZERO iframes at 390px: the element
  // starts well below a short viewport, the observer never fired, and the
  // section showed a heading with nothing under it. Desktop was fine, which is
  // exactly how it would have shipped.
  //
  // This project has now hit the same shape three times — a reveal that never
  // revealed, a check that never checked, and this. The rule that keeps coming
  // out of it: an optimisation may decide WHEN something loads, never WHETHER.
  // So there is also a scroll listener and a timer, and the content wins.
  useEffect(() => {
    const el = ref.current;
    if (!el) { setVisible(true); return; }

    const near = () => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight + 400 && r.bottom > -400;
    };
    const show = () => setVisible(true);

    if (near()) { show(); return; }

    let io: IntersectionObserver | undefined;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) show(); }),
        { rootMargin: "400px" }
      );
      io.observe(el);
    }

    const onScroll = () => { if (near()) show(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    // Nothing stays unloaded forever. A visitor who never scrolls still gets a
    // working page; the cost is one iframe they may not look at.
    const failsafe = window.setTimeout(show, 3000);

    return () => {
      io?.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.clearTimeout(failsafe);
    };
  }, []);

  if (!BOOKING_ENABLED) return null;

  return (
    <div ref={ref} style={{ marginTop: "28px" }}>
      {visible ? (
        <iframe
          // `brandColor` paints Cal's own accents — the selected day, the time-slot
          // borders, the confirm button — in the design's blue instead of Cal's
          // default black. The calendar is third-party content sitting inside a
          // designed page; matching the one colour it exposes is the difference
          // between an embed and a pasted-in widget.
          //
          // `theme=light` is pinned rather than left to the visitor's OS: this
          // page is white, and a dark calendar in the middle of it reads as
          // broken rather than as a preference.
          src={`${bookingUrl(slug)}?embed=true&theme=light&brandColor=${encodeURIComponent(color.blue)}`}
          title={slug === MAINTENANCE_SLUG ? "Schedule maintenance" : "Schedule a test ride"}
          data-cal-link={calLink(slug)}
          loading="lazy"
          style={{
            width: "100%",
            // Cal.com's page is taller than a viewport-relative guess, and it
            // GROWS: header, description, location and timezone, then the month
            // grid, then a time-slot list once a day is picked. At
            // clamp(560px,70vh,760px) the grid was cut off mid-row and the
            // contact form below read as overlapping it.
            //
            // Height is set from Cal's own postMessage (see the effect above)
            // and only falls back to this when the message never arrives —
            // tall enough for a full month plus a few slots, so the fallback is
            // usable rather than merely non-broken.
            height: `${height}px`,
            border: `1px solid ${color.border}`,
            borderRadius: radius.panel,
            display: "block",
            background: color.white,
          }}
        />
      ) : (
        // Reserves the calendar's height so nothing below it jumps when the
        // iframe arrives.
        <div
          style={{
            height: `${height}px`,
            border: `1px solid ${color.border}`,
            borderRadius: radius.panel,
            background: color.surface,
          }}
        />
      )}
    </div>
  );
}
